import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import PropertyCard from "@/components/PropertyCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertiesPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    listing_type: searchParams.get("listing_type") || "",
    property_type: "",
    city: searchParams.get("city") || "",
    bedrooms: "",
    min_price: "",
    max_price: ""
  });

  useEffect(() => {
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== "all" && value !== "any") params.append(key, value);
      });
      
      const response = await axios.get(`${API}/properties?${params.toString()}`);
      setProperties(response.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="pt-36 pb-20 min-h-screen bg-bone" data-testid="properties-page">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-8">
          <h1 className="font-heading text-4xl md:text-5xl font-medium text-charcoal mb-2">
            {t('properties.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('properties.subtitle')}
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-6 shadow-soft mb-8" data-testid="filters-section">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Select
              value={filters.listing_type}
              onValueChange={(value) => handleFilterChange("listing_type", value)}
            >
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder={t('properties.filters.buyRent')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('properties.filters.all')}</SelectItem>
                <SelectItem value="buy">{t('properties.filters.buy')}</SelectItem>
                <SelectItem value="rent">{t('properties.filters.rent')}</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.property_type}
              onValueChange={(value) => handleFilterChange("property_type", value)}
            >
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder={t('properties.filters.type')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('properties.filters.allTypes')}</SelectItem>
                <SelectItem value="house">{t('properties.filters.house')}</SelectItem>
                <SelectItem value="condo">{t('properties.filters.condo')}</SelectItem>
                <SelectItem value="apartment">{t('properties.filters.apartment')}</SelectItem>
                <SelectItem value="chalet">{t('properties.filters.chalet')}</SelectItem>
              </SelectContent>
            </Select>

            <Input
              placeholder={t('properties.filters.city')}
              className="rounded-xl"
              value={filters.city}
              onChange={(e) => handleFilterChange("city", e.target.value)}
            />

            <Select
              value={filters.bedrooms}
              onValueChange={(value) => handleFilterChange("bedrooms", value)}
            >
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder={t('properties.filters.beds')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">{t('properties.filters.any')}</SelectItem>
                <SelectItem value="1">1+</SelectItem>
                <SelectItem value="2">2+</SelectItem>
                <SelectItem value="3">3+</SelectItem>
                <SelectItem value="4">4+</SelectItem>
                <SelectItem value="5">5+</SelectItem>
              </SelectContent>
            </Select>

            <Input
              placeholder={t('properties.filters.minPrice')}
              type="number"
              className="rounded-xl"
              value={filters.min_price}
              onChange={(e) => handleFilterChange("min_price", e.target.value)}
            />

            <Input
              placeholder={t('properties.filters.maxPrice')}
              type="number"
              className="rounded-xl"
              value={filters.max_price}
              onChange={(e) => handleFilterChange("max_price", e.target.value)}
            />
          </div>
        </div>

        {/* Properties Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={`skeleton-${i}`} className="bg-white rounded-2xl h-96 animate-pulse"></div>
            ))}
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground text-lg">{t('properties.noResults')}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertiesPage;
